import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { rejectValue, flatPayout, flatTransfer } from 'redux/helpers';
import format from 'date-fns/format';

import {
  GET_PAYOUTS,
  GET_PAYOUTS_BY_ID,
  GET_TRANSFERS,
  GET_WEBHOOKS,
  POST_EXPORT_PAYOUTS,
  POST_EXPORT_TRANSACTIONS,
} from 'services/api';
import {
  PAYOUT,
  PAYOUT_ALL,
  PAYOUT_BY_ID,
  PAYOUT_SEARCH,
  PAYOUT_EXPORT,
  PAYOUT_TRANSACTIONS_EXPORT,
  PAYOUT_TRANSFERS,
  PAYOUT_WEBHOOKS,
} from './types';
import { last90Days } from 'components/DateRangePicker/ranges';
import { DATE_FOR_API } from 'utils/dateFormat';
import { showSuccessMessage } from '../notification';
import {
  STATUS_PROCESSED,
  STATUS_FAILED,
  STATUS_PENDING,
  STATUS_INITIATED,
  STATUS_CANCELLED,
  STATUS_IN_TRANSIT,
} from 'pages/Payouts/helpers';
const payoutAdapter = createEntityAdapter();

export const initialState = payoutAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(last90Days.startDate, DATE_FOR_API),
    to: format(last90Days.endDate, DATE_FOR_API),
    statusCheckbox: true,
    status: `${STATUS_PROCESSED},${STATUS_FAILED},${STATUS_PENDING},${STATUS_INITIATED},${STATUS_CANCELLED},${STATUS_IN_TRANSIT}`,
  },
  search: {
    term: '',
    submitted: false,
  },
});

export const getPayouts = createAsyncThunk(
  PAYOUT_ALL,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { statusCheckbox, status, ..._params } = getState().payout.params;
      let params = { ..._params };
      if (statusCheckbox && status) {
        params = { ...params, status };
      }
      const { data, meta } = await GET_PAYOUTS({
        ...params,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatPayout);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const getPayoutsById = createAsyncThunk(
  PAYOUT_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_PAYOUTS_BY_ID(id);

      return flatPayout(data);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchPayouts = createAsyncThunk(
  PAYOUT_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term } = getState().payout.search;

      if (!term) {
        return;
      }

      const { data, meta } = await GET_PAYOUTS({
        term,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatPayout);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportPayouts = createAsyncThunk(
  PAYOUT_EXPORT,
  async (_payout = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const email = getState().auth.user.email;
      const { statusCheckbox, status, per_page, page, ..._params } =
        getState().payout.params;
      const { term, submitted } = getState().payout.search;
      let params = {};

      if (_payout.id) {
        params.term = _payout.id;
      } else if (term && submitted) {
        params.term = term;
      } else {
        params = _params;
        if (statusCheckbox && status) {
          params = { ...params, status };
        }
      }

      await POST_EXPORT_PAYOUTS(params);
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportPayoutTransactions = createAsyncThunk(
  PAYOUT_TRANSACTIONS_EXPORT,
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const email = getState().auth.user.email;

      await POST_EXPORT_TRANSACTIONS({ payout_id: id });
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getPayoutTransfers = createAsyncThunk(
  PAYOUT_TRANSFERS,
  async ({ payout_id, page = 1 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const companyId = getState().company.current;

      const { data, meta } = await GET_TRANSFERS({
        payout_id,
        page,
        per_page: 10,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatTransfer);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getPayoutWebhooks = createAsyncThunk(
  PAYOUT_WEBHOOKS,
  async ({ payout_id, page = 1 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const companyId = getState().company.current;

      const { data, meta } = await GET_WEBHOOKS({
        payout_id,
        page,
        per_page: 10,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      return { data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const payoutSlice = createSlice({
  name: PAYOUT,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPayouts.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getPayouts.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPayouts.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payoutAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(searchPayouts.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchPayouts.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchPayouts.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payoutAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getPayoutsById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getPayoutsById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getPayoutsById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        payoutAdapter.upsertOne(state, payload);
      })
      .addCase(getPayoutTransfers.fulfilled, (state, action) => {
        payoutAdapter.updateOne(state, {
          id: action.meta.arg.payout_id,
          changes: {
            payouts: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addCase(getPayoutWebhooks.fulfilled, (state, action) => {
        payoutAdapter.updateOne(state, {
          id: action.meta.arg.payout_id,
          changes: {
            webhooks: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addDefaultCase((state) => state);
  },
});

export const payoutSelectors = payoutAdapter.getSelectors(
  (state) => state.payout
);

export const payoutByIdSelector = (state, id) => ({
  data: payoutSelectors.selectById(state, id),
  isLoading: state.payout.detailsIsLoading,
});

export const payoutAllSelector = (state) => ({
  data: payoutSelectors.selectAll(state),
  pagination: state.payout.pagination,
  isLoading: state.payout.isLoading,
  params: state.payout.params,
  searchTerm: state.payout.search.term,
});

export const { setParams, setSearchTerm, resetSearch } = payoutSlice.actions;

export default payoutSlice.reducer;
